import React from 'react'
import {
  AttributeList,
  FullCopyAttribute,
  CollapsibleInspectorSection,
} from '../../components'

import {
  StackingOrder,
  StackPadding,
  StackProperties,
} from '../../../../../../inspector/types/InspectorTypes/Stack'
import {
  CrossAxisAlignment,
  MainAxisAlignment,
  StackDirection,
} from '../../../../../../inspector'

import { Icon, PaddingItem, PaddingWrapper, WrapWrapper } from './Stack.styles'

// Icons
import { ReactComponent as HorizontalIcon } from '@sketch/icons/rectangle-2-vertical-17'
import { ReactComponent as VerticalIcon } from '@sketch/icons/rectangle-2-horizontal-17'
import { ReactComponent as WrapIcon } from '@sketch/icons/flex-wrap-17'
import { ReactComponent as LastOnTopIcon } from '@sketch/icons/rectangle-stack-17'

import { ReactComponent as JustifyStartIcon } from '@sketch/icons/flex-justify-start-17'
import { ReactComponent as JustifyCenterIcon } from '@sketch/icons/flex-justify-center-17'
import { ReactComponent as JustifyEndIcon } from '@sketch/icons/flex-justify-end-17'
import { ReactComponent as JustifyEvenlyIcon } from '@sketch/icons/flex-justify-evenly-17'
import { ReactComponent as JustifyAroundIcon } from '@sketch/icons/flex-justify-around-17'
import { ReactComponent as JustifyBetweenIcon } from '@sketch/icons/flex-justify-between-17'

import { ReactComponent as FillHIcon } from '@sketch/icons/flex-fill-h-17'
import { ReactComponent as FillVIcon } from '@sketch/icons/flex-fill-v-17'

import { ReactComponent as AlignStartIcon } from '@sketch/icons/flex-align-start-17'
import { ReactComponent as AlignCenterIcon } from '@sketch/icons/flex-align-center-17'
import { ReactComponent as AlignEndIcon } from '@sketch/icons/flex-align-end-17'

import { ReactComponent as PaddingPairedIcon } from '@sketch/icons/padding-paired-17'
import { ReactComponent as PaddingHorizontalIcon } from '@sketch/icons/padding-horizontal-11'
import { ReactComponent as PaddingLeftIcon } from '@sketch/icons/padding-left-11'

const mainAxisIcons = {
  [MainAxisAlignment.Start]: JustifyStartIcon,
  [MainAxisAlignment.Center]: JustifyCenterIcon,
  [MainAxisAlignment.End]: JustifyEndIcon,
  [MainAxisAlignment.SpaceAround]: JustifyAroundIcon,
  [MainAxisAlignment.SpaceBetween]: JustifyBetweenIcon,
  [MainAxisAlignment.SpaceEvenly]: JustifyEvenlyIcon,
} satisfies Record<MainAxisAlignment, React.ElementType>

type VisibleCrossAxisAlignment = Exclude<
  CrossAxisAlignment,
  CrossAxisAlignment.None
>

const crossAxisIcons = {
  [CrossAxisAlignment.Start]: AlignStartIcon,
  [CrossAxisAlignment.Center]: AlignCenterIcon,
  [CrossAxisAlignment.End]: AlignEndIcon,
  [CrossAxisAlignment.Stretch]: AlignStartIcon,
} satisfies Record<VisibleCrossAxisAlignment, React.ElementType>

const iconFor = <TAlignment extends string>(
  alignment: TAlignment,
  icons: Record<TAlignment, React.ElementType>
) => icons[alignment]

interface StackProps {
  stack: StackProperties
}

const renderDirection = (
  direction: StackDirection,
  wrappingEnabled: boolean
) => (
  <>
    {direction === StackDirection.Horizontal ? (
      <>
        <Icon as={HorizontalIcon} /> Horizontal
      </>
    ) : (
      <>
        <Icon as={VerticalIcon} /> Vertical
      </>
    )}
    {wrappingEnabled && (
      <WrapWrapper>
        <Icon as={WrapIcon} /> Wrap
      </WrapWrapper>
    )}
  </>
)

const renderStackingOrder = (stackingOrder: StackingOrder) => {
  switch (stackingOrder) {
    case StackingOrder.FirstOnTop:
      return (
        <>
          <Icon as={LastOnTopIcon} $flip /> First on top
        </>
      )
    case StackingOrder.LastOnTop:
      return (
        <>
          <Icon as={LastOnTopIcon} /> Last on top
        </>
      )
  }
}

// The terminology we use for main axis and cross axis alignment is different depending
// on the direction of the stack. For example, if the stack is horizontal, then the main
// axis is horizontal and the cross axis is vertical and vice versa.
//
// The two AxisTerm functions below return the appropriate term for the axis alignments
// based on the direction of the stack itself, ie `start` becomes either `Left` or `Top`.
const mainAxisTerm = (
  mainAxisAlignment: MainAxisAlignment,
  direction: StackDirection
) => {
  switch (mainAxisAlignment) {
    case MainAxisAlignment.Start:
      return direction === StackDirection.Horizontal ? 'Left' : 'Top'
    case MainAxisAlignment.Center:
      return direction === StackDirection.Horizontal ? 'Center' : 'Middle'
    case MainAxisAlignment.End:
      return direction === StackDirection.Horizontal ? 'Right' : 'Bottom'
    case MainAxisAlignment.SpaceAround:
      return 'Space Around'
    case MainAxisAlignment.SpaceBetween:
      return 'Space Between'
    case MainAxisAlignment.SpaceEvenly:
      return 'Space Evenly'
  }
}

const crossAxisTerm = (
  crossAxisAlignment: VisibleCrossAxisAlignment,
  direction: StackDirection
) => {
  switch (crossAxisAlignment) {
    case CrossAxisAlignment.Start:
      return direction === StackDirection.Horizontal ? 'Top' : 'Left'
    case CrossAxisAlignment.Center:
      return direction === StackDirection.Horizontal ? 'Middle' : 'Center'
    case CrossAxisAlignment.End:
      return direction === StackDirection.Horizontal ? 'Bottom' : 'Right'
    case CrossAxisAlignment.Stretch:
      return 'Stretch'
  }
}

const renderMainAxisAlignment = (
  direction: StackDirection,
  mainAxisAlignment: MainAxisAlignment
) => (
  <>
    <Icon
      as={iconFor(mainAxisAlignment, mainAxisIcons)}
      $rotate={direction === StackDirection.Vertical}
    />{' '}
    {mainAxisTerm(mainAxisAlignment, direction)}
  </>
)

const renderCrossAxisAlignment = (
  direction: StackDirection,
  crossAxisAlignment: VisibleCrossAxisAlignment
) => (
  <>
    <Icon
      as={iconFor(crossAxisAlignment, crossAxisIcons)}
      $rotate={direction === StackDirection.Horizontal}
    />{' '}
    {crossAxisTerm(crossAxisAlignment, direction)}
  </>
)

const renderGap = (
  direction: StackDirection,
  gap: number,
  mainAxisAlignment: MainAxisAlignment
) => {
  if (
    [
      MainAxisAlignment.SpaceAround,
      MainAxisAlignment.SpaceBetween,
      MainAxisAlignment.SpaceEvenly,
    ].includes(mainAxisAlignment)
  ) {
    return 'auto'
  }

  if (direction === StackDirection.Horizontal) {
    return (
      <>
        <Icon as={FillHIcon} /> {gap}px
      </>
    )
  }

  return (
    <>
      <Icon as={FillVIcon} /> {gap}px
    </>
  )
}

const renderPadding = (padding: StackPadding) => {
  const uniquePaddings = new Set([
    padding.top,
    padding.right,
    padding.bottom,
    padding.left,
  ])

  if (uniquePaddings.size === 1) {
    return (
      <>
        <Icon as={PaddingPairedIcon} /> {padding.top}px
      </>
    )
  } else if (padding.top === padding.bottom && uniquePaddings.size === 2) {
    return (
      <PaddingWrapper>
        <PaddingItem>
          <Icon as={PaddingHorizontalIcon} /> {padding.left}px
        </PaddingItem>
        <PaddingItem>
          <Icon as={PaddingHorizontalIcon} /> {padding.top}px
        </PaddingItem>
      </PaddingWrapper>
    )
  }

  return (
    <PaddingWrapper>
      <PaddingItem>
        <Icon as={PaddingLeftIcon} /> {padding.left}px
      </PaddingItem>
      <PaddingItem>
        <Icon as={PaddingLeftIcon} /> {padding.top}px
      </PaddingItem>
      <PaddingItem>
        <Icon as={PaddingLeftIcon} /> {padding.right}px
      </PaddingItem>
      <PaddingItem>
        <Icon as={PaddingLeftIcon} /> {padding.bottom}px
      </PaddingItem>
    </PaddingWrapper>
  )
}

const copyValuePadding = (padding: StackPadding) => {
  const uniquePaddings = new Set([
    padding.top,
    padding.right,
    padding.bottom,
    padding.left,
  ])

  if (uniquePaddings.size === 1) {
    return `${padding.top}px`
  } else if (padding.top === padding.bottom && uniquePaddings.size === 2) {
    return `${padding.top}px ${padding.right}px`
  }

  return `${padding.top}px ${padding.right}px ${padding.bottom}px ${padding.left}px`
}

const flipDirection = (direction: StackDirection) => {
  return direction === StackDirection.Horizontal
    ? StackDirection.Vertical
    : StackDirection.Horizontal
}

export const Stack = ({ stack }: StackProps) => {
  const {
    crossAxisAlignment,
    direction,
    gap,
    mainAxisAlignment,
    padding,
    wrappingEnabled,
    alignContent,
    crossAxisGutterGap,
    stackingOrder,
    bordersAffectLayout,
  } = stack

  return (
    <CollapsibleInspectorSection
      identifier="stack"
      title="Stack"
      data-testid="inspector-sidebar-stack"
    >
      <AttributeList>
        <FullCopyAttribute
          label="Direction"
          value={renderDirection(direction, wrappingEnabled)}
          copyValue={direction}
        />

        <FullCopyAttribute
          label="Align"
          value={renderMainAxisAlignment(direction, mainAxisAlignment)}
          copyValue={mainAxisTerm(mainAxisAlignment, direction)}
        />

        <FullCopyAttribute
          label="Gap"
          value={renderGap(direction, gap, mainAxisAlignment)}
          copyValue={`${gap}px`}
        />

        {crossAxisAlignment !== CrossAxisAlignment.None && (
          <FullCopyAttribute
            label="Cross Align"
            value={renderCrossAxisAlignment(direction, crossAxisAlignment)}
            copyValue={crossAxisTerm(crossAxisAlignment, direction)}
          />
        )}

        <FullCopyAttribute
          label="Padding"
          value={renderPadding(padding)}
          copyValue={copyValuePadding(padding)}
        />

        {wrappingEnabled && (
          <>
            <FullCopyAttribute
              label="Multi-line"
              value={renderMainAxisAlignment(
                flipDirection(direction),
                alignContent
              )}
              copyValue={alignContent}
            />
            <FullCopyAttribute
              label=""
              value={renderGap(
                flipDirection(direction),
                crossAxisGutterGap,
                alignContent
              )}
              copyValue={`${crossAxisGutterGap}px`}
            />
          </>
        )}

        {stackingOrder && (
          <FullCopyAttribute
            label="Layering"
            value={renderStackingOrder(stackingOrder)}
            copyValue={
              stackingOrder === StackingOrder.LastOnTop
                ? 'Last on top'
                : 'First on top'
            }
          />
        )}

        {bordersAffectLayout && (
          <FullCopyAttribute label="Borders" value="Included in layout" />
        )}
      </AttributeList>
    </CollapsibleInspectorSection>
  )
}
